import React, { useState } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkle, Calculator, Lightning } from '@phosphor-icons/react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import HelpHint from '@/components/HelpHint';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';

const STRATEGY_LABELS = {
  none: { label: 'No Conversions', description: 'Status quo — let RMDs run' },
  fill_12: { label: 'Fill 12% Bracket', description: 'Conservative — convert up to top of 12% bracket each year' },
  fill_22: { label: 'Fill 22% Bracket', description: 'Moderate — convert up to top of 22% bracket each year' },
  fill_24: { label: 'Fill 24% Bracket', description: 'Aggressive — convert up to top of 24% bracket each year' },
  custom: { label: 'Custom Amount', description: 'Convert a fixed amount each year' }
};

const RothConversion = () => {
  useTrackToolUsage('roth_conversion');
  const [inputs, setInputs] = useState({
    starting_traditional_balance: 500000,
    current_age: 60,
    rmd_start_age: 73,
    other_annual_income: 50000,
    annual_growth_rate: 0.06,
    filing_status: 'single',
    strategy: 'fill_22',
    custom_annual_conversion: 0,
    years_to_project: 15
  });
  const [result, setResult] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    setLoading(true);
    try {
      const [analyzeRes, compareRes] = await Promise.all([
        api.post('/analyzers/roth-conversion', inputs),
        api.post('/analyzers/roth-conversion/compare', inputs)
      ]);
      setResult(analyzeRes.data);
      setComparison(compareRes.data.comparison);
      toast.success('Analysis complete');
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Multi-Year Analyzer:</strong> Models Roth conversion strategies across multiple years using 2024 federal tax brackets. Simplified — does not include state taxes, NIIT, or full IRMAA modeling. Consult a tax professional before executing conversions.
          </p>
        </div>
      </div>

      <div>
        <div className="flex items-center gap-3">
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">
            Roth Conversion Analyzer
          </h1>
          <HelpHint
            topic="How Roth Conversion Analyzer works"
            query="roth conversion traditional RMD tax bracket"
            testid="roth-help-hint"
          />
        </div>
        <p className="text-base text-[#4A5568]">
          Multi-year strategy modeling with tax-bracket optimization
        </p>
      </div>

      {/* Inputs */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="roth-inputs-card">
        <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Scenario Inputs</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="balance">Starting Traditional Balance ($)</Label>
            <Input id="balance" type="number" data-testid="roth-balance-input" value={inputs.starting_traditional_balance}
              onChange={(e) => setInputs({ ...inputs, starting_traditional_balance: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="age">Current Age</Label>
            <Input id="age" type="number" data-testid="roth-age-input" value={inputs.current_age}
              onChange={(e) => setInputs({ ...inputs, current_age: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="years">Years to Project</Label>
            <Input id="years" type="number" data-testid="roth-years-input" value={inputs.years_to_project}
              onChange={(e) => setInputs({ ...inputs, years_to_project: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="other_income">Other Annual Income ($)</Label>
            <Input id="other_income" type="number" data-testid="roth-other-income-input" value={inputs.other_annual_income}
              onChange={(e) => setInputs({ ...inputs, other_annual_income: Number(e.target.value) })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="growth">Annual Growth Rate (%)</Label>
            <Input id="growth" type="number" step="0.01" data-testid="roth-growth-input" value={inputs.annual_growth_rate * 100}
              onChange={(e) => setInputs({ ...inputs, annual_growth_rate: Number(e.target.value) / 100 })} className="border-slate-300" />
          </div>
          <div>
            <Label htmlFor="filing">Filing Status</Label>
            <Select value={inputs.filing_status} onValueChange={(v) => setInputs({ ...inputs, filing_status: v })}>
              <SelectTrigger data-testid="roth-filing-select" className="border-slate-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single</SelectItem>
                <SelectItem value="head_of_household">Head of Household</SelectItem>
                <SelectItem value="married_filing_jointly">Married Filing Jointly</SelectItem>
                <SelectItem value="married_filing_separately">Married Filing Separately</SelectItem>
                <SelectItem value="qualifying_widow">Qualifying Widow(er)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="strategy">Conversion Strategy</Label>
            <Select value={inputs.strategy} onValueChange={(v) => setInputs({ ...inputs, strategy: v })}>
              <SelectTrigger data-testid="roth-strategy-select" className="border-slate-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STRATEGY_LABELS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#4A5568] mt-1">{STRATEGY_LABELS[inputs.strategy].description}</p>
          </div>
          {inputs.strategy === 'custom' && (
            <div>
              <Label htmlFor="custom">Custom Annual Conversion ($)</Label>
              <Input id="custom" type="number" data-testid="roth-custom-input" value={inputs.custom_annual_conversion}
                onChange={(e) => setInputs({ ...inputs, custom_annual_conversion: Number(e.target.value) })} className="border-slate-300" />
            </div>
          )}
        </div>
        <Button onClick={handleAnalyze} disabled={loading} data-testid="roth-analyze-button" className="mt-6 bg-[#0A1128] hover:bg-[#152145]">
          <Calculator size={18} className="mr-2" />
          {loading ? 'Analyzing...' : 'Run Multi-Year Analysis'}
        </Button>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Total Converted</p>
              <p className="text-2xl font-semibold text-[#C5A059] ibm-plex">${result.summary.total_converted.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Conversion Tax Paid</p>
              <p className="text-2xl font-semibold text-[#C53030] ibm-plex">${result.summary.total_conversion_tax.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Final Traditional Balance</p>
              <p className="text-2xl font-semibold text-[#4A5568] ibm-plex">${result.summary.final_traditional_balance.toLocaleString()}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5 dashboard-card">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Final Roth Balance</p>
              <p className="text-2xl font-semibold text-[#2D6A4F] ibm-plex">${result.summary.final_roth_balance.toLocaleString()}</p>
            </div>
          </div>

          {/* Year-by-year Chart */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="roth-yearly-chart">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Account Balances Over Time</h2>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={result.yearly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="age" stroke="#4A5568" label={{ value: 'Age', position: 'insideBottom', offset: -5 }} />
                <YAxis stroke="#4A5568" />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="traditional_balance_end" name="Traditional Balance" stroke="#4A5568" strokeWidth={2} />
                <Line type="monotone" dataKey="roth_balance_end" name="Roth Balance" stroke="#C5A059" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Strategy Comparison */}
          {comparison && (
            <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card" data-testid="roth-comparison-chart">
              <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Strategy Comparison</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {comparison.map((c, idx) => (
                  <div key={idx} className={`border rounded-md p-4 ${c.strategy === inputs.strategy ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-slate-200'}`}>
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568] mb-1">{STRATEGY_LABELS[c.strategy].label}</p>
                    <p className="text-sm text-[#4A5568] mb-2">Tax: <span className="font-semibold text-[#C53030] ibm-plex">${c.summary.total_conversion_tax.toLocaleString()}</span></p>
                    <p className="text-sm text-[#4A5568] mb-2">Final Roth: <span className="font-semibold text-[#2D6A4F] ibm-plex">${c.summary.final_roth_balance.toLocaleString()}</span></p>
                    <p className="text-sm text-[#4A5568]">Savings: <span className={`font-semibold ibm-plex ${c.summary.estimated_future_tax_savings > 0 ? 'text-[#2D6A4F]' : 'text-[#C53030]'}`}>${c.summary.estimated_future_tax_savings.toLocaleString()}</span></p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Year-by-year Table */}
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Year-by-Year Detail</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.08em] text-[#4A5568]">
                    <th className="text-left py-2 px-2">Age</th>
                    <th className="text-right py-2 px-2">Conversion</th>
                    <th className="text-right py-2 px-2">RMD</th>
                    <th className="text-right py-2 px-2">Taxable Income</th>
                    <th className="text-right py-2 px-2">Marginal</th>
                    <th className="text-right py-2 px-2">Tax</th>
                    <th className="text-right py-2 px-2">Traditional</th>
                    <th className="text-right py-2 px-2">Roth</th>
                  </tr>
                </thead>
                <tbody>
                  {result.yearly.map(y => (
                    <tr key={y.year} className="border-b border-slate-100">
                      <td className="py-2 px-2">{y.age}</td>
                      <td className="py-2 px-2 text-right text-[#C5A059] ibm-plex">${y.conversion_amount.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right ibm-plex">${y.rmd_amount.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right ibm-plex">${y.taxable_income.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right font-medium">{y.marginal_rate}%</td>
                      <td className="py-2 px-2 text-right text-[#C53030] ibm-plex">${y.total_tax.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right ibm-plex">${y.traditional_balance_end.toLocaleString()}</td>
                      <td className="py-2 px-2 text-right text-[#2D6A4F] ibm-plex font-medium">${y.roth_balance_end.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RothConversion;
